import axios from 'axios'
import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { useUser } from '../../store/userState'
import { uploadFileToS3 } from '../../middlewares/awsBucket'
import { Spinner, Tooltip } from '@material-tailwind/react'
import {
  ArrowUpTrayIcon,
  BuildingLibraryIcon,
  CheckIcon,
  EyeIcon,
  EyeSlashIcon,
  LockClosedIcon,
  PencilSquareIcon,
  UserIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid'
import AppShell from '../../Components/AppShell'
import PageHeader from '../../Components/PageHeader'

const ProfSettings = () => {
  const navigate = useNavigate()
  const [isDone, setIsDone] = useState(true)
  const [isAltered, setIsAltered] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isEditable, setIsEditable] = useState(false)
  const [openResetPw, setOpenResetPw] = useState(false)
  const [jwtToken, setJwtToken] = useState('')
  const [userImgUrl, setUserImgUrl] = useState('')
  const [profImageLink, setProfImageLink] = useState('')
  const [defaultAvatar, setDefaultAvatar] = useState('')
  const [editCredentials, setEditCredentials] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  })
  const [toggleShowPw, setToggleShowPw] = useState({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false,
  })

  const { user } = useUser()
  const permission = user.permissions[0].department
  const avatarLink = `https://nexus-portal.s3.amazonaws.com/profileImages/${userImgUrl}`
  const fallbackAvatar =
    'https://cdn.pixabay.com/photo/2016/08/08/09/17/avatar-1577909_1280.png'

  const handleEditable = () => setIsEditable(!isEditable)
  const handleOpenResetPw = () => {
    setOpenResetPw(!openResetPw)
    setJwtToken(localStorage.jwt)
  }
  const handleTogglePw = (field) => {
    setToggleShowPw((prevState) => ({
      ...prevState,
      [field]: !prevState[field],
    }))
  }

  useEffect(() => {
    axios
      .get(`/api/users/get-user/${user._id}`)
      .then((response) => {
        if (response.data.message) {
          console.log('Cannot get users')
        } else {
          setUserImgUrl(response.data.profileImage)
        }
      })
      .catch((error) => {
        console.error('Error:', error)
      })
  }, [isAltered, user._id])

  const handleImageUpload = (event) => {
    const file = event.target.files[0]
    if (file) {
      const reader = new FileReader()
      reader.onloadend = async () => {
        setIsLoading(true)
        setDefaultAvatar(reader.result)
        setProfImageLink(`${user._id}.${file.name}`)
        await uploadFileToS3('profileImages', file, user._id)
        setIsDone(false)
        setIsLoading(false)
        setIsAltered((prevState) => !prevState)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleSave = () => {
    axios
      .put(`/api/users/update-user/${user._id}`, { profileImage: profImageLink })
      .then((response) => {
        if (response.data.message) {
          toast.success(`${response.data.message}`)
          setIsEditable(!isEditable)
          setIsAltered((prevState) => !prevState)
          setIsDone(true)
          setIsLoading(true)
        } else {
          toast.error(`${response.data.message}`)
        }
        setIsLoading(false)
      })
      .catch((error) => {
        console.error('Error:', error)
        const errorMessage = error.response?.data?.message || 'An error occurred during add.'
        toast.error(errorMessage)
        setIsLoading(false)
      })
  }

  const triggerFileInput = () => {
    document.getElementById('avatar-upload').click()
  }

  const handleLogout = () => {
    localStorage.removeItem('jwt')
    localStorage.removeItem('id')
    navigate('/login')
  }

  const handleResetPw = () => {
    const userCreds = {
      _id: user.id,
      token: jwtToken,
      oldPassword: editCredentials.oldPassword,
      newPassword: editCredentials.newPassword,
      confirmNewPassword: editCredentials.confirmPassword,
    }

    axios
      .post(`/api/users/auth/change-password`, userCreds)
      .then((response) => {
        if (response.data.message) {
          toast.success(`${response.data.message}`)
          setEditCredentials({
            oldPassword: '',
            newPassword: '',
            confirmPassword: '',
          })
          setOpenResetPw(false)
          if (window.confirm('Password reset successful! Do you want to log out?')) {
            handleLogout()
          }
        } else {
          toast.error(`${response.data.message}`)
        }
      })
      .catch((error) => {
        console.error('Error:', error)
        const errorMessage = error.response?.data?.message || 'Cannot reset password.'
        toast.error(errorMessage)
      })
  }

  return (
    <AppShell>
      <PageHeader
        kicker="Account"
        title="Profile settings"
        subtitle="Update your avatar and password for this Nexus V2 account."
        crumbs={[{ label: 'Home', to: '/home' }, { label: 'Profile' }]}
      />

      <div className="mx-auto grid max-w-3xl gap-6">
        <section className="nx-panel overflow-hidden">
          <div className="h-28 bg-gradient-to-r from-surface via-raised to-surface border-b border-rule" />
          <div className="relative px-6 pb-8 pt-0">
            <div className="relative -mt-14 flex flex-col items-center sm:items-start">
              <div className="relative">
                {isEditable && (
                  <Tooltip content="Upload avatar profile">
                    <button
                      type="button"
                      className="absolute -right-1 bottom-2 z-10 rounded-full border border-rule bg-raised p-1.5 text-signal"
                      onClick={triggerFileInput}
                      aria-label="Upload avatar"
                    >
                      <ArrowUpTrayIcon className="h-4 w-4" />
                    </button>
                  </Tooltip>
                )}
                <input
                  type="file"
                  id="avatar-upload"
                  className="hidden"
                  onChange={handleImageUpload}
                  accept=".jpg, .png, .jpeg"
                />
                <div className="h-28 w-28 overflow-hidden rounded-full border-4 border-raised bg-rule">
                  <img
                    src={userImgUrl ? avatarLink : defaultAvatar || fallbackAvatar}
                    className="h-full w-full object-cover"
                    alt="avatar"
                  />
                </div>
                {isEditable && (
                  <Tooltip content="Image preview">
                    <div
                      className={`absolute -left-4 top-8 h-14 w-14 overflow-hidden rounded-full border-4 border-raised bg-rule ${
                        isLoading ? 'animate-pulse' : ''
                      }`}
                    >
                      <img
                        src={defaultAvatar || fallbackAvatar}
                        className="h-full w-full object-cover"
                        alt="preview"
                      />
                    </div>
                  </Tooltip>
                )}
                {isEditable ? (
                  <div className="absolute -bottom-2 left-1/2 flex -translate-x-1/2 gap-2">
                    <button
                      type="button"
                      className="rounded-full bg-success p-1.5 text-night disabled:opacity-50"
                      disabled={isDone}
                      onClick={handleSave}
                      aria-label="Save avatar"
                    >
                      {isLoading ? (
                        <Spinner className="h-4 w-4" />
                      ) : (
                        <CheckIcon className="h-4 w-4" />
                      )}
                    </button>
                    <button
                      type="button"
                      className="rounded-full bg-danger p-1.5 text-night"
                      onClick={handleEditable}
                      aria-label="Cancel edit"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <Tooltip content="Edit avatar">
                    <button
                      type="button"
                      className="absolute -right-1 bottom-2 rounded-full border border-rule bg-raised p-1.5 text-success"
                      onClick={handleEditable}
                      aria-label="Edit avatar"
                    >
                      <PencilSquareIcon className="h-4 w-4" />
                    </button>
                  </Tooltip>
                )}
              </div>

              <div className="mt-6 text-center sm:text-left">
                <h2 className="font-display text-3xl tracking-wide text-paper">
                  {user.firstName} {user.lastName}
                </h2>
                <div className="mt-3 flex flex-wrap justify-center gap-2 sm:justify-start">
                  <span className="nx-chip-info inline-flex items-center gap-1">
                    <UserIcon className="h-3.5 w-3.5" />
                    {user.role}
                  </span>
                  <span className="nx-chip-idle inline-flex items-center gap-1">
                    <BuildingLibraryIcon className="h-3.5 w-3.5" />
                    {permission}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="nx-panel p-6">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="font-display text-xl tracking-wide text-paper">Password</h3>
              <p className="text-sm text-muted">Change your sign-in password for this account.</p>
            </div>
            <button type="button" onClick={handleOpenResetPw} className="nx-btn-danger">
              {openResetPw ? 'Cancel' : 'Reset password'}
              <LockClosedIcon className="h-4 w-4" />
            </button>
          </div>

          {openResetPw && (
            <form
              className="mt-5 grid gap-3 sm:max-w-md"
              onSubmit={(e) => {
                e.preventDefault()
                handleResetPw()
              }}
            >
              {['oldPassword', 'newPassword', 'confirmPassword'].map((field) => (
                <div key={field} className="relative">
                  <label className="nx-label" htmlFor={field}>
                    {field === 'oldPassword'
                      ? 'Old password'
                      : field === 'newPassword'
                        ? 'New password'
                        : 'Confirm password'}
                  </label>
                  <input
                    id={field}
                    type={toggleShowPw[field] ? 'text' : 'password'}
                    onChange={(e) =>
                      setEditCredentials({ ...editCredentials, [field]: e.target.value })
                    }
                    className="nx-input pr-10"
                    required
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-8 text-muted hover:text-paper"
                    onClick={() => handleTogglePw(field)}
                    aria-label={`Toggle ${field} visibility`}
                  >
                    {toggleShowPw[field] ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
              ))}
              <button type="submit" className="nx-btn-primary w-fit">
                Save password
              </button>
            </form>
          )}
        </section>
      </div>
    </AppShell>
  )
}

export default ProfSettings
